<template>
  <div class="flex flex-col space-y-3" v-if="space">
    <div class="grid grid-cols-3 gap-4" v-if="assignedQrCodes.length > 0">
      <div
        @click="handleAddCode"
        class="flex items-center justify-center border border-dashed border-gray-300 rounded-md cursor-pointer"
        :class="{
          'w-32 h-32': assignedQrCodes.length < 1,
          'mb-7': assignedQrCodes.length > 0 && canUpdateQrCodes,
          '!cursor-not-allowed opacity-60': disabled,
          'hover:border-gray-500 group': !disabled,
        }"
      >
        <PlusIcon class="h-5 w-5 text-gray-500 group-hover:text-gray-900" />
      </div>
      <div
        v-for="qr in assignedQrCodes"
        :key="qr.id"
        class="flex flex-col items-center"
      >
        <img
          :src="qr.image_url"
          alt=""
          class="border border-gray-200 rounded-md aspect-square"
        />
        <button
          v-if="canUpdateQrCodes"
          type="button"
          class="text-red-600 mt-1"
          @click="askRemovalConfirmation(qr.id)"
        >
          <MinusCircleIcon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </div>
    <NoResults
      v-else
      image="/images/empty/assets/qr_codes.svg"
      :title="t('components.empty_states.asset.qr_codes.title')"
      :description="t('components.empty_states.asset.qr_codes.description')"
      :button-label="t('components.empty_states.asset.qr_codes.action')"
      @click="handleAddCode"
      :hideButton="() => !props.showAddButton"
    />

    <scan-qr-modal
      :open="isScanningQrCode"
      :space="space?.id"
      @close="isScanningQrCode = false"
      @decode="isScanningQrCode = false"
      @update:open="(value) => (isScanningQrCode = value)"
    />
    <ConfirmationModal
      :open="!!qrCodeBeingRemoved"
      @close="qrCodeBeingRemoved = null"
      @confirm="handleRemovalConfirmed"
      :title="t('admin.unassign_qr_code.dialog.title')"
      :confirmationLabel="t('components.buttons.confirm')"
      :cancelLabel="t('components.buttons.cancel')"
    >
    </ConfirmationModal>
  </div>
</template>

<script setup>
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import { MinusCircleIcon } from "@heroicons/vue/24/outline";
import { useWebView } from "@/Tailwind/Mixins/useWebView";
import { computed, inject, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import ScanQrModal from "@/Tailwind/Components/ScanQrModal.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import ConfirmationModal from "@/Tailwind/Components/ConfirmationModal.vue";
import { validate as isUuid4 } from "uuid";
import axios from "axios";
import NoResults from "@/Tailwind/Components/ListView/NoResults.vue";

const props = defineProps({
  space: Object,
  disabled: Boolean,
  showAddButton: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();
const { runningInWebView, getWebViewBridge } = useWebView();
const { hasPermission, permissions } = inject("permissions");
const canUpdateQrCodes = computed(() => hasPermission(permissions.updateSpace));

const isScanningQrCode = ref(false);
const qrCodeBeingRemoved = ref(null);

const assignedQrCodes = computed(() => props.space?.qr_codes ?? []);

onMounted(() => {
  if (runningInWebView()) {
    getWebViewBridge().addEventListener(
      "onQrCodeScanned",
      handleWebviewQrCodeScanned
    );
  }
});

onUnmounted(() => {
  if (runningInWebView()) {
    getWebViewBridge().removeEventListener(
      "onQrCodeScanned",
      handleWebviewQrCodeScanned
    );
  }
});

async function handleWebviewQrCodeScanned(e) {
  getWebViewBridge().qrScannerDismissModal();
  const { id } = e.detail;

  const { uuid } = await verifyQrCode(id);
  router.put(route("qr_code.update", uuid), {
    space_id: props.space.id,
  });
}

async function verifyQrCode(id) {
  // If something other than a UUID is decoded, we assume a QR code
  // with a shortened URL was scanned.
  if (!isUuid4(id)) {
    const {
      data: { url },
    } = await axios.get(route("s.resolve", id));
    id = url.split("/").pop();
  }

  const {
    data: { assigned, space, id: uuid },
  } = await axios.get(route("qr_code.verify", id));

  return {
    assigned,
    space,
    uuid,
  };
}

function handleAddCode() {
  if (props.disabled) {
    return;
  }

  if (runningInWebView()) {
    getWebViewBridge().qrScannerOpenModal({
      title: t("admin.scan_qr_code.title"),
      invalid_qr_code: "Invalid QR code",
      back_button_text: t("components.buttons.back"),
    });

    return;
  }

  isScanningQrCode.value = true;
}

const askRemovalConfirmation = (id) => (qrCodeBeingRemoved.value = id);
const handleRemovalConfirmed = () => {
  router.delete(route("qr_code.unassign", qrCodeBeingRemoved.value));
  qrCodeBeingRemoved.value = null;
};
</script>
